import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { UserRole } from "@/types";
import { useAuth } from "@/context/AuthContext";
import { GetUserResponseDto } from "@/dto/get-user-response.dto";

import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import InviteUserForm from "@/components/users/InviteUserForm";
import { BulkInviteModal } from "@/components/users/BulkInviteModal";
import ProjectInvitationRow from "@/components/ProjectInvitationRow";
import { Roles } from "@/enums/roles.enum";
import { userApi } from "@/services/userService";
import { unifiedInvitationService, UnifiedInvitation } from "@/services/unifiedInvitationService";
import { DeleteUserDialog } from "@/components/users/DeleteUserDialog";
import { Search, Home, Settings, Mail, Clock, UserCheck, Trash2, RotateCcw, Users2, Calendar, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { UserDetailsModal } from "@/components/users/UserDetailsModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { projectApi } from "@/services/projectService";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import MobileCard from "@/components/mobile/MobileCard";
import { useDeviceType } from "@/hooks/use-mobile";
import { format } from "date-fns";

const Users = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isMobile } = useDeviceType();

  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [mobileActiveTab, setMobileActiveTab] = useState("users");
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const highlightedUserId = searchParams.get("highlightedUser");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const { data: usersData, isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => userApi.getUsers(),
    enabled: true,
  });

  const {
    data: globalInvitations,
    isLoading: globalInvitationsLoading,
    error: globalInvitationsError,
  } = useQuery({
    queryKey: ["allUnifiedInvitations"],
    queryFn: () => unifiedInvitationService.getAllUnifiedInvitations(),
    enabled:
      !!currentUser && [Roles.ADMIN, Roles.VENDOR].includes(currentUser.role),
  });

  // Mutations for invitation management
  const deleteGlobalInviteMutation = useMutation({
    mutationFn: (inviteId: string) => unifiedInvitationService.deleteGlobalInvitation(inviteId),
    onMutate: (inviteId) => {
      setDeletingId(inviteId);
    },
    onSuccess: () => {
      toast({
        title: "Invitation deleted",
        description: "The invitation has been successfully deleted.",
      });
      queryClient.invalidateQueries({ queryKey: ["allUnifiedInvitations"] });
    },
    onError: (error: unknown) => {
      console.error("Delete invitation error:", error);
      toast({
        title: "Error",
        description: 
          (error && typeof error === 'object' && 'response' in error && 
           error.response && typeof error.response === 'object' && 'data' in error.response &&
           error.response.data && typeof error.response.data === 'object' && 'error' in error.response.data)
            ? String((error.response.data as { error: string }).error)
            : "Failed to delete invitation. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  const deleteProjectInviteMutation = useMutation({
    mutationFn: (inviteId: string) => unifiedInvitationService.deleteProjectInvitation(inviteId),
    onMutate: (inviteId) => {
      setDeletingId(inviteId);
    },
    onSuccess: () => {
      toast({
        title: "Invitation deleted",
        description: "The invitation has been successfully deleted.",
      });
      queryClient.invalidateQueries({ queryKey: ["allProjectInvitations"] });
    },
    onError: (error: unknown) => {
      console.error("Delete project invitation error:", error);
      toast({
        title: "Error",
        description: 
          (error && typeof error === 'object' && 'response' in error && 
           error.response && typeof error.response === 'object' && 'data' in error.response &&
           error.response.data && typeof error.response.data === 'object' && 'error' in error.response.data)
            ? String((error.response.data as { error: string }).error)
            : "Failed to delete invitation. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  const resendGlobalInviteMutation = useMutation({
    mutationFn: (inviteId: string) => unifiedInvitationService.resendGlobalInvitation(inviteId),
    onMutate: (inviteId) => {
      setResendingId(inviteId);
    },
    onSuccess: () => {
      toast({
        title: "Invitation resent",
        description: "The invitation has been successfully resent.",
      });
      queryClient.invalidateQueries({ queryKey: ["allUnifiedInvitations"] });
    },
    onError: (error: unknown) => {
      console.error("Resend invitation error:", error);
      toast({
        title: "Error",
        description: 
          (error && typeof error === 'object' && 'response' in error && 
           error.response && typeof error.response === 'object' && 'data' in error.response &&
           error.response.data && typeof error.response.data === 'object' && 'error' in error.response.data)
            ? String((error.response.data as { error: string }).error)
            : "Failed to resend invitation. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setResendingId(null);
    },
  });

  const resendProjectInviteMutation = useMutation({
    mutationFn: (inviteId: string) => unifiedInvitationService.resendProjectInvitation(inviteId),
    onMutate: (inviteId) => {
      setResendingId(inviteId);
    },
    onSuccess: () => {
      toast({
        title: "Invitation resent",
        description: "The invitation has been successfully resent.",
      });
      queryClient.invalidateQueries({ queryKey: ["allProjectInvitations"] });
    },
    onError: (error: unknown) => {
      console.error("Resend project invitation error:", error);
      toast({
        title: "Error",
        description: 
          (error && typeof error === 'object' && 'response' in error && 
           error.response && typeof error.response === 'object' && 'data' in error.response &&
           error.response.data && typeof error.response.data === 'object' && 'error' in error.response.data)
            ? String((error.response.data as { error: string }).error)
            : "Failed to resend invitation. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setResendingId(null);
    },
  });

  // New mutation for deleting individual projects from invitations
  const deleteProjectFromInviteMutation = useMutation({
    mutationFn: ({ invitationId, projectId }: { invitationId: string; projectId: string }) => 
      unifiedInvitationService.deleteProjectFromInvitation(invitationId, projectId),
    onMutate: ({ invitationId, projectId }) => {
      setDeletingId(`${invitationId}-${projectId}`);
    },
    onSuccess: (data) => {
      toast({
        title: data.entireInvitationDeleted ? "Invitation deleted" : "Project removed",
        description: data.entireInvitationDeleted 
          ? "The invitation has been completely deleted as it was the last project."
          : "The project has been removed from the invitation.",
      });
      queryClient.invalidateQueries({ queryKey: ["allUnifiedInvitations"] });
      queryClient.invalidateQueries({ queryKey: ["allProjectInvitations"] });
    },
    onError: (error: unknown) => {
      console.error("Delete project from invitation error:", error);
      toast({
        title: "Error",
        description: 
          (error && typeof error === 'object' && 'response' in error && 
           error.response && typeof error.response === 'object' && 'data' in error.response &&
           error.response.data && typeof error.response.data === 'object' && 'error' in error.response.data)
            ? String((error.response.data as { error: string }).error)
            : "Failed to remove project from invitation. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  const getRoleBadgeVariant = (role: UserRole) => {
    switch (role) {
      case Roles.ADMIN:
        return "destructive";
      case Roles.VENDOR:
        return "default";
      case Roles.CONTRACTOR:
        return "secondary";
      case Roles.CLIENT:
      default:
        return "outline";
    }
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const isPending = (invitation: {
    acceptedAt: string | null;
    expiresAt: string;
  }) => {
    return !invitation.acceptedAt && !isExpired(invitation.expiresAt);
  };

  // Check if current user can invite users
  const canInviteUsers = () => {
    if (!currentUser) return false;
    return [Roles.ADMIN, Roles.VENDOR].includes(currentUser.role);
  };

  // Check if current user can manage users
  const canManageUsers = () => {
    if (!currentUser) return false;
    return [Roles.ADMIN, Roles.VENDOR].includes(currentUser.role);
  };

  // Check if current user can delete invite
  const canDeleteInvite = (invitation: { invitedBy: { _id: string } | null }) => {
    if (!currentUser) return false;
    if (currentUser.role === Roles.ADMIN) return true;
    // Allow the person who sent the invite to delete it
    return invitation.invitedBy?._id === currentUser._id;
  };

  const openUserDetailsModal = (userId: string) => {
    setSelectedUserId(userId);
    setUserDetailsOpen(true);
  };

  const userRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});

  useEffect(() => {
    if (highlightedUserId && userRefs.current[highlightedUserId]) {
      userRefs.current[highlightedUserId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("highlightedUser");
      setTimeout(() => {
        navigate({ search: newParams.toString() }, { replace: true });
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers =
    usersData &&
    usersData?.users?.filter((user) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        `${user.firstName} ${user.lastName}`
          .toLowerCase()
          .includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.companyName?.toLowerCase().includes(searchLower)
      );
    });

  // Combine global and project invitations for the invites tab
  const allInvitations = globalInvitations || [];

  // Mobile components
  const MobileUserCard = ({ user }: { user: GetUserResponseDto }) => (
    <MobileCard
      onClick={() => openUserDetailsModal(user._id)}
      className="cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Users2 className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-sm">
                {user.firstName} {user.lastName}
              </h3>
              <p className="text-xs text-muted-foreground">{user.email}</p>
              <p className="text-xs text-muted-foreground">{user.companyName}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={user.role === Roles.ADMIN ? "default" : "secondary"} className="h-6 px-2 text-xs uppercase tracking-wide shadow-sm">
            {user.role}
          </Badge>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </MobileCard>
  );

  const MobileInvitationCard = ({ invitation }: { invitation: UnifiedInvitation }) => (
    <MobileCard>
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                <Mail className="h-4 w-4 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm">{invitation.email}</h3>
                <p className="text-xs text-muted-foreground">
                  {invitation.isGlobalInvite ? 'Global Invitation' : `Project: ${invitation.projects[0]?.projectId?.name || 'Unknown'}`}
                </p>
              </div>
            </div>
          </div>
          <Badge 
            variant={
              invitation.acceptedAt 
                ? "default" 
                : isExpired(invitation.expiresAt) 
                ? "destructive" 
                : "secondary"
            }
            className="h-6 px-2 text-xs uppercase tracking-wide shadow-sm"
          >
            {invitation.acceptedAt ? 'Accepted' : isExpired(invitation.expiresAt) ? 'Expired' : 'Pending'}
          </Badge>
        </div>
        
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <span className="text-muted-foreground">Role:</span>
            <span className="ml-1 font-medium">{invitation.role}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Invited:</span>
            <span className="ml-1 font-medium">
              {format(new Date(invitation.createdAt), "MMM d")}
            </span>
          </div>
        </div>

        {canDeleteInvite(invitation) && (
          <div className="flex gap-2 pt-2">
            {!invitation.acceptedAt && !isExpired(invitation.expiresAt) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => resendGlobalInviteMutation.mutate(invitation._id)}
                disabled={resendingId === invitation._id}
                className="flex-1"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                {resendingId === invitation._id ? 'Resending...' : 'Resend'}
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={deletingId === invitation._id}
                  className="flex-1"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  {deletingId === invitation._id ? 'Deleting...' : 'Delete'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Invitation</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this invitation? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      if (!invitation.isGlobalInvite) {
                        deleteProjectInviteMutation.mutate(invitation._id);
                      } else {
                        deleteGlobalInviteMutation.mutate(invitation._id);
                      }
                    }}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    </MobileCard>
  );

  const MobileTabs = ({ activeTab, setActiveTab }: { activeTab: string; setActiveTab: (tab: string) => void }) => (
    <div className="space-y-4">
      {/* Mobile tab switcher */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === "users" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveTab("users")}
          className="flex-1"
        >
          <Users2 className="h-4 w-4 mr-2" />
          Users ({filteredUsers?.length || 0})
        </Button>
        <Button
          variant={activeTab === "invites" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveTab("invites")}
          className="flex-1"
        >
          <Mail className="h-4 w-4 mr-2" />
          Invites ({allInvitations.length})
        </Button>
      </div>

      {/* Tab content */}
      {activeTab === "users" && (
        <div className="space-y-4">
          {/* Mobile search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Users list */}
          <div className="space-y-3">
            {isLoading ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Loading users...</p>
              </div>
            ) : filteredUsers && filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <MobileUserCard key={user._id} user={user} />
              ))
            ) : (
              <div className="text-center py-8">
                <Users2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No users found</h3>
                <p className="text-muted-foreground">
                  {searchTerm ? "Try adjusting your search terms." : "No users available."}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "invites" && (
        <div className="space-y-4">
          {globalInvitationsLoading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Loading invitations...</p>
            </div>
          ) : allInvitations.length > 0 ? (
            <div className="space-y-3">
              {allInvitations.map((invitation) => (
                <MobileInvitationCard key={invitation._id} invitation={invitation} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No invitations</h3>
              <p className="text-muted-foreground">No pending invitations found.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {isMobile ? (
        <div className="space-y-4">
          {/* Mobile header */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">Users</h1>
            {canInviteUsers() && (
              <div className="flex gap-2">
                <InviteUserForm />
                <Button variant="outline" size="sm" onClick={() => setBulkInviteOpen(true)}>
                  Bulk
                </Button>
              </div>
            )}
          </div>

          {/* Mobile tabs */}
          <MobileTabs activeTab={mobileActiveTab} setActiveTab={setMobileActiveTab} />
        </div>
      ) : (
        <div className="space-y-6">
          <nav
            className="flex items-center text-sm text-muted-foreground"
            aria-label="Breadcrumb"
          >
            <Link
              to="/"
              className="hover:underline text-primary font-medium flex items-center"
            >
              <Home className="h-4 w-4 mr-1" />
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground font-semibold">Users</span>
          </nav>

      <Tabs defaultValue="users" className="w-full">
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-4 -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="w-full justify-start h-16 p-2 bg-slate-100/80 dark:bg-muted/40 rounded-2xl border shadow-inner gap-2">
            <TabsTrigger 
              value="users" 
              className="flex-1 md:flex-none h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground"
            >
              <Users2 className="h-4 w-4 mr-2.5" />
              Users
            </TabsTrigger>
            <TabsTrigger 
              value="invites" 
              className="flex-1 md:flex-none h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground"
            >
              <Mail className="h-4 w-4 mr-2.5" />
              Invitations
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="users">
          <Card className="border-border/50 shadow-sm relative overflow-hidden">
            {/* Shiny Highlight */}
            <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-foreground/10 to-transparent opacity-50" />
            
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">User Management</h1>
                  <p className="text-sm text-muted-foreground mt-1">Manage system users, roles, and invitations.</p>
                </div>
                {canInviteUsers() && (
                  <div className="flex items-center gap-2">
                    <InviteUserForm />
                    <Button 
                      variant="outline" 
                      onClick={() => setBulkInviteOpen(true)}
                      className="shadow-sm hover:shadow transition-all"
                    >
                      Bulk Invite
                    </Button>
                  </div>
                )}
              </div>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold tracking-tight">All Users</h2>
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                className="pl-8 bg-background/50 border-border/50 focus:ring-primary/20 transition-all hover:border-border/80"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="rounded-xl border border-border/50 overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-semibold text-foreground">Name</TableHead>
                  <TableHead className="font-semibold text-foreground">Email</TableHead>
                  <TableHead className="font-semibold text-foreground">Company</TableHead>
                  <TableHead className="font-semibold text-foreground">Role</TableHead>
                  <TableHead className="font-semibold text-foreground">Joined</TableHead>
                  <TableHead className="w-[140px] font-semibold text-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  // Skeleton rows while loading
                  Array.from({ length: 5 }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 w-12 bg-muted animate-pulse rounded" />
                      </TableCell>
                      <TableCell>
                        <div className="h-8 w-8 bg-muted animate-pulse rounded-full mx-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : !isLoading && usersData && usersData.users.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-6 text-muted-foreground"
                    >
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers?.map((user) => (
                    <TableRow
                      key={user._id}
                      ref={(el) => (userRefs.current[user._id] = el)}
                      className={`transition-colors ${
                        highlightedUserId === user._id ? "animate-blink" : ""
                      }`}
                    >
                      <TableCell className="font-medium">
                        {`${user.firstName} ${user.lastName}`.trim()}
                      </TableCell>
                      <TableCell className="font-medium">
                        {user.email}
                      </TableCell>
                      <TableCell>{user.companyName || "—"}</TableCell>
                      <TableCell>
                        {user.superAdmin ? (
                          <Badge
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700 h-6 px-2 text-xs uppercase tracking-wide shadow-sm"
                          >
                            Super Admin
                          </Badge>
                        ) : (
                          <Badge variant={getRoleBadgeVariant(user.role)} className="h-6 px-2 text-xs uppercase tracking-wide shadow-sm">
                            {user.role}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(user.createdAt).toLocaleString("en", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {canManageUsers() && !user.superAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openUserDetailsModal(user._id)}
                              className="h-8 px-2 text-xs"
                            >
                              <Settings className="h-3 w-3 mr-1" />
                              Manage
                            </Button>
                          )}
                          {user.superAdmin ? (
                            <div className="flex items-center justify-center">
                              <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                                Protected
                              </span>
                            </div>
                          ) : (
                            <DeleteUserDialog
                              userId={user._id}
                              userName={`${user.firstName} ${user.lastName}`.trim()}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="invites">
          <Card className="border-border/50 shadow-sm relative overflow-hidden">
            {/* Shiny Highlight */}
            <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-foreground/10 to-transparent opacity-50" />
            
            <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              All Invitations
            </h2>
          </div>

          <div className="rounded-xl border border-border/50 overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-semibold text-foreground">Email</TableHead>
                  <TableHead className="font-semibold text-foreground">Projects</TableHead>
                  <TableHead className="font-semibold text-foreground">Invited By</TableHead>
                  <TableHead className="font-semibold text-foreground">Status</TableHead>
                  <TableHead className="font-semibold text-foreground">Created</TableHead>
                  <TableHead className="w-[100px] font-semibold text-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {globalInvitationsLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                      </TableCell>
                      <TableCell>
                        <div className="h-6 w-6 bg-muted animate-pulse rounded" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : globalInvitationsError ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-6 text-muted-foreground"
                    >
                      Failed to load invitations. Please try refreshing the
                      page.
                    </TableCell>
                  </TableRow>
                ) : !globalInvitations || globalInvitations.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-6 text-muted-foreground"
                    >
                      No invitations found.
                    </TableCell>
                  </TableRow>
                ) : (
                  globalInvitations.map((invitation) => (
                    <ProjectInvitationRow
                      key={invitation._id}
                      invitation={invitation}
                      onDelete={deleteGlobalInviteMutation.mutate}
                      onResend={resendProjectInviteMutation.mutate}
                      onDeleteProject={(projectId) => 
                        deleteProjectFromInviteMutation.mutate({ invitationId: invitation._id, projectId })
                      }
                      isDeletingInvitation={deletingId === invitation._id}
                      isResending={resendingId === invitation._id}
                      isDeletingProject={(projectId) => deletingId === `${invitation._id}-${projectId}`}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bulk Invite Modal */}
      <BulkInviteModal open={bulkInviteOpen} onOpenChange={setBulkInviteOpen} />

      {/* User Details Modal */}
      {selectedUserId && (
        <UserDetailsModal
          open={userDetailsOpen}
          onOpenChange={setUserDetailsOpen}
          userId={selectedUserId}
        />
      )}
        </div>
      )}
    </>
  );
};

export default Users;
